<template>
  <el-dialog
    v-model="visible"
    :title="isPin ? 'Change Salary PIN' : 'Change Password'"
    width="450px"
    :close-on-click-modal="false"
    @close="handleClose"
  >
    <el-form
      ref="formRef"
      :model="form"
      :rules="rules"
      label-position="top"
      @submit.prevent="onSubmit"
    >
      <el-form-item :label="isPin ? 'Current PIN' : 'Current password'" prop="current">
        <el-input
          v-model="form.current"
          :type="showCurrent ? 'text' : 'password'"
          :placeholder="isPin ? 'Enter current 4-digit PIN' : 'Enter current password'"
          :maxlength="isPin ? 4 : 128"
          size="large"
        >
          <template #suffix>
            <el-icon class="password-eye" @click="showCurrent = !showCurrent">
              <View v-if="!showCurrent" />
              <Hide v-else />
            </el-icon>
          </template>
        </el-input>
      </el-form-item>

      <el-form-item :label="isPin ? 'New PIN' : 'New password'" prop="next">
        <el-input
          v-model="form.next"
          :type="showNext ? 'text' : 'password'"
          :placeholder="isPin ? 'Enter new 4-digit PIN' : 'At least 8 chars, start with capital letter or digit'"
          :maxlength="isPin ? 4 : 128"
          size="large"
        >
          <template #suffix>
            <el-icon class="password-eye" @click="showNext = !showNext">
              <View v-if="!showNext" />
              <Hide v-else />
            </el-icon>
          </template>
        </el-input>
      </el-form-item>

      <el-form-item :label="isPin ? 'Confirm PIN' : 'Confirm password'" prop="confirm">
        <el-input
          v-model="form.confirm"
          :type="showConfirm ? 'text' : 'password'"
          :placeholder="isPin ? 'Confirm new 4-digit PIN' : 'Repeat new password'"
          :maxlength="isPin ? 4 : 128"
          size="large"
        >
          <template #suffix>
            <el-icon class="password-eye" @click="showConfirm = !showConfirm">
              <View v-if="!showConfirm" />
              <Hide v-else />
            </el-icon>
          </template>
        </el-input>
      </el-form-item>
    </el-form>

    <template #footer>
      <div class="dialog-footer">
        <el-button @click="handleClose">Cancel</el-button>
        <el-button type="primary" :loading="loading" @click="onSubmit">
          Save
        </el-button>
      </div>
    </template>
  </el-dialog>
</template>

<script setup>
import { ref, reactive, computed, watch } from "vue";
import { View, Hide } from "@element-plus/icons-vue";
import { ElMessage } from "element-plus";
import {
  verifySalaryPin,
  setSalaryPin,
} from "@/modules/Finance/pages/AdminSalary/api/admin-salary.api";
import { changePassword } from "@/modules/ChangePassword/api/change-password.api";

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  mode: {
    type: String,
    default: "pin",
    validator: (v) => ["pin", "password"].includes(v),
  },
});

const emit = defineEmits(["update:modelValue", "success"]);

const visible = ref(props.modelValue);
const isPin = computed(() => props.mode === "pin");

watch(
  () => props.modelValue,
  (val) => {
    visible.value = val;
  }
);

watch(visible, (val) => {
  emit("update:modelValue", val);
});

const formRef = ref(null);
const loading = ref(false);
const showCurrent = ref(false);
const showNext = ref(false);
const showConfirm = ref(false);

const form = reactive({
  current: "",
  next: "",
  confirm: "",
});

const resetForm = () => {
  form.current = "";
  form.next = "";
  form.confirm = "";
  showCurrent.value = false;
  showNext.value = false;
  showConfirm.value = false;
  formRef.value?.resetFields();
};

const handleClose = () => {
  resetForm();
  visible.value = false;
};

const validateCurrent = (rule, value, callback) => {
  if (isPin.value) {
    if (!/^\d{4}$/.test(value)) {
      callback(new Error("PIN must be exactly 4 digits"));
      return;
    }
  } else if (!value) {
    callback(new Error("Enter current password"));
    return;
  }
  callback();
};

const validateNext = (rule, value, callback) => {
  if (isPin.value) {
    if (!/^\d{4}$/.test(value)) {
      callback(new Error("PIN must be exactly 4 digits"));
      return;
    }
  } else {
    if (!value || value.length < 8) {
      callback(new Error("Password must be at least 8 characters"));
      return;
    }
    if (!/^[A-Z0-9]/.test(value)) {
      callback(new Error("Password must start with a capital letter or digit"));
      return;
    }
  }
  if (form.confirm) {
    formRef.value?.validateField("confirm");
  }
  callback();
};

const validateConfirm = (rule, value, callback) => {
  if (value !== form.next) {
    callback(new Error(isPin.value ? "PINs do not match" : "Passwords do not match"));
    return;
  }
  callback();
};

const rules = computed(() => ({
  current: [
    { required: true, message: isPin.value ? "Enter current PIN" : "Enter current password", trigger: "blur" },
    { validator: validateCurrent, trigger: "blur" },
  ],
  next: [
    { required: true, message: isPin.value ? "Enter new PIN" : "Enter new password", trigger: "blur" },
    { validator: validateNext, trigger: "blur" },
  ],
  confirm: [
    { required: true, message: isPin.value ? "Confirm new PIN" : "Confirm new password", trigger: "blur" },
    { validator: validateConfirm, trigger: "blur" },
  ],
}));

const onSubmit = async () => {
  if (!formRef.value) return;
  await formRef.value.validate(async (valid) => {
    if (!valid) return;
    loading.value = true;
    try {
      if (isPin.value) {
        await verifySalaryPin(form.current);
        await setSalaryPin(form.next, form.confirm);
        ElMessage.success("Salary PIN changed successfully");
      } else {
        await changePassword({
          current_password: form.current,
          new_password: form.next,
          confirm_password: form.confirm,
        });
        ElMessage.success("Password changed successfully");
      }
      resetForm();
      visible.value = false;
      emit("success");
    } catch (error) {
      const detail = error?.response?.data?.detail ?? error?.response?.data?.error;
      if (detail === "Wrong PIN") {
        ElMessage.error("Current PIN is incorrect");
      } else if (typeof detail === "string") {
        ElMessage.error(detail);
      } else {
        ElMessage.error(isPin.value ? "Failed to change PIN" : "Failed to change password");
      }
    } finally {
      loading.value = false;
    }
  });
};
</script>

<style scoped>
.password-eye {
  cursor: pointer;
}

.dialog-footer {
  padding-bottom: 12px;
  padding-right: 12px;
}
</style>

<style>
.el-overlay .el-dialog .el-input__wrapper {
  box-shadow: 0 0 0 1px #dcdfe6 inset !important;
  background-color: #fff !important;
  border: 1px solid #dcdfe6 !important;
}

.el-overlay .el-dialog .el-input__inner {
  color: #303133 !important;
  -webkit-text-fill-color: #303133 !important;
}
</style>
